import { createOffline } from "@redux-offline/redux-offline";
import offlineConfig from "@redux-offline/redux-offline/lib/defaults";
import { isObject } from "lodash";
import { applyMiddleware, createStore } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { END } from "redux-saga";

import {
  ADD_WEIGHT_FROM_SCALES,
  DEVICE_MESSAGE_RESPONSE,
  SELL_FILE,
  SELL_REVERSAL,
  TAKE_FILE,
  TAKE_REVERSAL,
} from "constants/actionTypes";

import { getLogRocketMiddleware } from "lib/logRocket/middleware";

import getDiscard from "offline/discard";
import { getEffect } from "offline/effect";
import queue from "offline/queue";
import retry from "offline/retry";
import rootReducer from "reducers";
import { persistOptions } from "store/persistConfig";
import { sagaMiddleware } from "store/util";

import {
  crashReporterMiddleware,
  wrongSaleDiscarderMiddleware,
} from "./middleware";
// To make redux devtools better, dont serialize known huge objects.
// https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/Troubleshooting.md#excessive-use-of-memory-and-cpu
const ACTIONS_TO_IGNORE = {
  GET_PROPERTIES_SUCCESS: "properties",
  GET_BUSINESSES_SUCCESS: "businesses",
  GET_SCANS_SUCCESS: "scans",
  [SELL_FILE.FETCH_BULK.SUCCESS]: "sellFiles",
  [SELL_FILE.FETCH.SUCCESS]: "sellFiles",
  [SELL_FILE.CREATE.SUCCESS]: "sellFiles",
  [SELL_REVERSAL.FETCH_BULK.SUCCESS]: "sellReversals",
  [SELL_REVERSAL.FETCH.SUCCESS]: "sellReversals",
  [SELL_REVERSAL.CREATE.SUCCESS]: "sellReversals",
  [TAKE_FILE.FETCH_BULK.SUCCESS]: "takeFiles",
  [TAKE_FILE.FETCH.SUCCESS]: "takeFiles",
  [TAKE_FILE.CREATE.SUCCESS]: "takeFiles",
  [TAKE_REVERSAL.FETCH_BULK.SUCCESS]: "takeReversals",
  [TAKE_REVERSAL.FETCH.SUCCESS]: "takeReversals",
  [TAKE_REVERSAL.CREATE.SUCCESS]: "takeReversals",
};

const sanitizeSlice = (state, key) => {
  const sliceState = state[key];
  const sanitized = {};
  const groupKeys = ["byId", key];
  groupKeys.forEach(groupKey => {
    if (isObject(sliceState) && groupKey in sliceState) {
      sanitized[groupKey] = { removedInSanitize: true };
    }
  });
  return {
    [key]: Object.assign({}, sliceState, sanitized),
  };
};

const stateSanitizer = state => {
  return Object.assign(
    {},
    state,
    sanitizeSlice(state, "properties"),
    sanitizeSlice(state, "businesses"),
    sanitizeSlice(state, "businessesV2"),
    {
      auth: { removedInSanitize: true },
    },
    // Scans
    {
      scanners: {
        ...state.scanners,
        scans: { removedInSanitize: true },
      },
    },
    // NLIS
    {
      nlisFiles: {
        takeFiles: {
          ...state.nlisFiles.takeFiles,
          byId: {
            removedInSanitize: true,
          },
        },
        takeReversals: {
          ...state.nlisFiles.takeReversals,
          byId: {
            removedInSanitize: true,
          },
        },
        sellFiles: {
          ...state.nlisFiles.sellFiles,
          byId: {
            removedInSanitize: true,
          },
        },
        sellReversals: {
          ...state.nlisFiles.sellReversals,
          byId: {
            removedInSanitize: true,
          },
        },
      },
    },
  );
};

const actionSanitizer = action =>
  ACTIONS_TO_IGNORE[action.type] && action[ACTIONS_TO_IGNORE[action.type]]
    ? { ...action, [ACTIONS_TO_IGNORE[action.type]]: "<<LONG_BLOB>>" }
    : action;

const actionsDenylist = [ADD_WEIGHT_FROM_SCALES, DEVICE_MESSAGE_RESPONSE];

const reduxDevtoolsExtensionOptions = {
  actionSanitizer,
  actionsDenylist,
  stateSanitizer,
};

export default function configureStore(persistCallback, getStore) {
  const ourOfflineConfig = {
    ...offlineConfig,
    persistCallback,
    persistOptions,
    effect: getEffect(getStore),
    discard: getDiscard(getStore),
    queue,
    retry,
  };

  const {
    middleware: offlineMiddleware,
    enhanceReducer,
    enhanceStore,
  } = createOffline(ourOfflineConfig);

  const composeEnhancers = composeWithDevTools(reduxDevtoolsExtensionOptions);

  const middlewareList = [
    sagaMiddleware,
    crashReporterMiddleware,
    wrongSaleDiscarderMiddleware,
    offlineMiddleware,
  ];

  const logRocketMiddleware = getLogRocketMiddleware();
  if (logRocketMiddleware) {
    middlewareList.push(logRocketMiddleware);
  }

  const middleware = applyMiddleware(...middlewareList);

  const store = createStore(
    enhanceReducer(rootReducer),
    composeEnhancers(enhanceStore, middleware),
  );

  if (window.Cypress) {
    window.store = store;
  }

  store.runSaga = sagaMiddleware.run;
  store.close = () => store.dispatch(END);

  return store;
}
